import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Global 401 handler: redirect to /login instead of throwing uncaught error overlay.
// IMPORTANT: Skip /auth/me and /auth/refresh — those 401s are normal during initial
// session probing and we let the components handle them via state (setUser(false)).
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const url = err.config?.url || "";
      const isAuthProbe = url.includes("/auth/me") || url.includes("/auth/refresh") || url.includes("/auth/login");
      const path = window.location.pathname;
      const onLogin = path === "/login" || path.startsWith("/login");
      if (!isAuthProbe && !onLogin) {
        window.location.replace("/login");
      }
    }
    return Promise.reject(err);
  }
);

export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo salió mal. Inténtalo de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const CLP = (v) =>
  new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v || 0);

export const FREQUENCIES = [
  { value: "monthly", label: "Mensual" },
  { value: "bimonthly", label: "Bimestral" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "annual", label: "Anual" },
  { value: "custom", label: "Personalizada" },
];

export const SERVICE_STATUS = [
  { value: "scheduled", label: "Programado" },
  { value: "in_progress", label: "En curso" },
  { value: "pending_review", label: "En revisión" },
  { value: "completed", label: "Completado" },
  { value: "cancelled", label: "Cancelado" },
];

// Tipos de servicio. Backward-compatible: los registros viejos con "maintenance" o "repair" se
// mapean automáticamente al mostrar (ver `SERVICE_TYPE_ALIASES`).
export const SERVICE_TYPES = [
  { value: "mantencion_recurrente", label: "Mantención recurrente", color: "#00C853" },
  { value: "mantencion_unica",      label: "Mantención única",      color: "#1976D2" },
  { value: "reparacion",            label: "Reparación",            color: "#D50000" },
  { value: "instalacion",           label: "Instalación",           color: "#FF5A00" },
  { value: "visita_tecnica",        label: "Visita técnica",        color: "#7B1FA2" },
];
export const SERVICE_TYPE_ALIASES = { maintenance: "mantencion_recurrente", repair: "reparacion" };
export const normalizeServiceType = (v) => SERVICE_TYPE_ALIASES[v] || v || "mantencion_recurrente";

export const QUOTE_STATUS = [
  { value: "draft", label: "Borrador" },
  { value: "sent", label: "Enviada" },
  { value: "approved", label: "Aprobada" },
  { value: "rejected", label: "Rechazada" },
  { value: "converted", label: "Convertida" },
];

export const PAYMENT_STATUS = [
  { value: "pending", label: "Pendiente" },
  { value: "paid", label: "Pagado" },
  { value: "overdue", label: "Vencido" },
  { value: "cancelled", label: "Cancelado" },
];

export const INVOICE_STATUS = [
  { value: "por_facturar", label: "Por Facturar" },
  { value: "facturado", label: "Facturado" },
  { value: "pagado", label: "Pagado" },
];

// Etiquetas para el filtro (incluye vista virtual "vencido")
export const INVOICE_FILTER_STATUS = [
  { value: "por_facturar", label: "Por Facturar" },
  { value: "facturado", label: "Facturado" },
  { value: "vencido", label: "Vencido" },
  { value: "pagado", label: "Pagado" },
];

export const INVOICE_SOURCES = [
  { value: "manual", label: "Manual" },
  { value: "quote", label: "Cotización" },
  { value: "maintenance", label: "Mantención" },
  { value: "repair", label: "Reparación" },
  { value: "sale", label: "Venta" },
  { value: "other", label: "Otro" },
];

export const IVA_RATE = 0.19; // IVA fijo 19% Chile
